/**
 * web/src/components/layout/ErrorBoundary.tsx
 * Single root error boundary (CONTEXT.md "Claude's Discretion" — Error
 * boundary scope: single root). Catches render errors anywhere in the
 * tree and shows a friendly recovery UI with a Try-again + Reload button.
 *
 * Per CLAUDE.md conventions: per-file JSDoc + per-export JSDoc. A class
 * component is required because React 19 still scopes
 * componentDidCatch/getDerivedStateFromError to class components — there
 * is no equivalent hook today.
 *
 * Threat T-05-05-02 (Information disclosure): error.message and
 * info.componentStack are logged via console.error to the browser console
 * only — never transmitted. The user's own console; no network exfiltration.
 */
import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { Empty } from '../shared/Empty';
import { Button } from '../shared/Button';

/** Props for ErrorBoundary. */
interface Props {
  children: ReactNode;
  /** Optional render-prop fallback. Receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

/** Internal state — null when healthy, populated on caught render error. */
interface State {
  error: Error | null;
}

/**
 * Single-root error boundary. Catches render errors anywhere below and
 * renders a friendly recovery surface with two CTAs: "Try again" (clears
 * the error and retries the subtree) and "Reload" (full page reload).
 *
 * Mounted once at the app root (main.tsx wraps `<App />`). Not used as a
 * per-route boundary in V1 — see CONTEXT.md "Claude's Discretion."
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  /**
   * React lifecycle: derive state from a thrown error so the next render
   * can show the fallback UI instead of the broken subtree.
   */
  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  /**
   * React lifecycle: log the error + componentStack to the browser console
   * for debugging. NOT transmitted to the server (T-05-05-02 mitigation).
   */
  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Render error:', error, info.componentStack);
  }

  /** Clears the error state so the children re-render fresh. */
  reset = (): void => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <div className="min-h-screen flex items-center justify-center p-8 bg-bg-soft">
          <Empty
            title="Something went wrong"
            description={
              <span>
                The dashboard hit an unexpected error. Reload the page to recover. If the issue
                persists, check the browser console and contact #pixel-dev on Slack.
              </span>
            }
            action={
              <div className="flex gap-2 mt-4">
                <Button variant="default" onClick={this.reset}>
                  Try again
                </Button>
                <Button variant="primary" onClick={() => window.location.reload()}>
                  Reload
                </Button>
              </div>
            }
          />
        </div>
      );
    }
    return this.props.children;
  }
}
